// lib
import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

// components
import { Dropdown } from 'primereact/dropdown'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

// styles
import style from '@aytm/app/stats/page/sidebar/view-options/styles/ViewOptionsPanel.module.scss'

const Translations = ({ root, model, data, features, onTranslationChange }) => {
  const { translation } = model
  const { country, locale, targetingLocales, totalVerbatims, totalVerbatimsByLocale, translationsMissing } = data

  const [translationMessage, setTranslationMessage] = useState(null)
  const [translationMessageType, setTranslationMessageType] = useState(null)

  const [showTransationPopup, setShowTransationPopup] = useState(true)

  const translationsOptions = useMemo(() => {
    if (!targetingLocales) return []

    const languageNames = new Intl.DisplayNames(['en'], { type: 'language' })

    const researcherLanguage = locale.split('_', 1)[0].toLowerCase()
    const surveyLanguages = targetingLocales.map(item => item.split('_', 1)[0].toLowerCase())

    const showOriginalLangsLabel =
      'WRD' != country.id && (1 < surveyLanguages.length || !surveyLanguages.some(item => 'en' == item))
    const originalLabel = `Original ${
      showOriginalLangsLabel ? `(${surveyLanguages.join(', ').toUpperCase()})` : 'verbatims'
    }`
    const originalText = `Show original ${
      showOriginalLangsLabel ? `(${surveyLanguages.join(', ').toUpperCase()})` : 'verbatims'
    }`
    const translations = [{ key: '*', text: originalText, label: originalLabel, icon: 'translate' }]

    if (
      ('en' == researcherLanguage && surveyLanguages.some(item => 'en' != item)) ||
      ('en' != researcherLanguage && (surveyLanguages.some(v => v != researcherLanguage) || 'WRD' != country.id))
    ) {
      translations.push({
        key: 'en',
        text: `<b class="p-text-icon">EN</b> Show verbatims in ${languageNames.of('en')}`,
        label: `<b class="p-text-icon">EN</b> Verbatims in ${languageNames.of('en')}`,
        translationsMissing: Boolean(translationsMissing?.en)
      })
    }

    if ('en' != researcherLanguage && surveyLanguages.some(v => v != researcherLanguage)) {
      translations.push({
        key: researcherLanguage,
        text: `<b class="p-text-icon">${researcherLanguage.toUpperCase()}</b> Show verbatims in ${languageNames.of(
          researcherLanguage
        )}`,
        label: `<b class="p-text-icon">${researcherLanguage.toUpperCase()}</b> Verbatims in ${languageNames.of(
          researcherLanguage
        )}`,
        translationsMissing: Boolean(translationsMissing?.[researcherLanguage])
      })
    }

    return translations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country.id, locale, targetingLocales, translationsMissing, features.ai_assistants_oe_translation.disabled])

  const itemTemplate = option =>
    option.hidden ? null : (
      <span
        id={`stat-view-options-translation-${'*' == option.key ? 'original' : option.key}`}
        className={classNames(
          'stat-view-options-translation',
          `stat-view-options-translation-${'*' == option.key ? 'original' : option.key}`,
          '-dtr-stat-view-options-translation',
          `-dtr-stat-view-options-translation-${'*' == option.key ? 'original' : option.key}`,
          `p-lvl-${option.lvl || 0}`,
          { 'p-item-label': option.icon }
        )}
        data-stat-translation={'*' == option.value ? 'original' : option.value}
        onClick={features.ai_assistants_oe_translation.disabled ? null : () => onTranslationChange(option.key)}
      >
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
        {option.translationsMissing ? <i className={`icon-aytmfd-skipper ${style['skipper-icon']}`} /> : null}
      </span>
    )

  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }}></span>
        {option.translationsMissing && (
          <Popup
            className={style['component-popup']}
            width="270px"
            trigger={
              null != translationMessage && 0 < translationMessage.length && showTransationPopup ? 'hover' : 'none'
            }
            direction="bottomleft"
            popupOffset={14}
            portal
            show={showTransationPopup ? null : false}
            content={() => (
              <>
                <div className={style['component-popup-content']}>
                  {'skipper' == translationMessageType ? (
                    <span className={style['component-popup-icon']}>
                      <i className="icon-aytmfd-skipper" />
                    </span>
                  ) : 'error' == translationMessageType ? (
                    <span className={style['component-popup-icon']}>🧭</span>
                  ) : (
                    <span className={style['component-popup-icon']}>🙌</span>
                  )}
                  <span dangerouslySetInnerHTML={{ __html: translationMessage }}></span>
                </div>
              </>
            )}
          >
            <i className={`icon-aytmfd-skipper ${style['skipper-icon']} ${style['skipper-icon-spinned']}`} />
          </Popup>
        )}
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  useEffect(() => {
    const translationsMissingKeys = Object.keys(translationsMissing || {})

    if (translationsMissingKeys.includes(translation)) {
      const verbatimsByLocaleKey = Object.keys(totalVerbatimsByLocale || {}).find(
        locale => locale.split('_', 1)[0].toLowerCase() == translation
      )
      const total =
        totalVerbatims -
        (totalVerbatimsByLocale && Object.hasOwn(totalVerbatimsByLocale, 'verbatimsByLocaleKey')
          ? totalVerbatimsByLocale[verbatimsByLocaleKey].total
          : 0)
      setTranslationMessage(
        0 < total
          ? `<b>Work in progress!</b><br />Translating ${
              total - translationsMissing[translation]
            }/${total} verbatims to <b>${translation.toUpperCase()}</b><br />All originals are preserved`
          : `<b>Work in progress!</b><br />Translating ${
              translationsMissing[translation]
            } verbatims to <b>${translation.toUpperCase()}</b><br />All originals are preserved`
      )
      setTranslationMessageType('skipper')
    } else {
      setTranslationMessage(null)
      setTranslationMessageType(null)
    }
  }, [translation, totalVerbatims, totalVerbatimsByLocale, translationsMissing])

  return 1 < translationsOptions.length ? (
    features.ai_assistants_oe_translation.available ? (
      <>
        <div className={`${style.row} ${style['section-title']}`}>Verbatim&nbsp;translations</div>
        <div className={`${style.row} ${style['row-skipper']}`}>
          <div className="p-col">
            <a
              className="p-cmoponent p-button p-button-rounded p-button-stat"
              href={features.ai_assistants_oe_translation.onPackage.upgradeURL}
              rel="noreferrer"
            >
              <i className="icon-aytmfd-skipper" />
              {features.ai_assistants_oe_translation.onPackage.text.replace(' for ', ' ')}
            </a>
          </div>
          <SidebarPopup
            id="unlock-translation"
            root={root}
            help={`<span><b>${
              features.ai_assistants_oe_translation.onPackage.text
            }</b><br />Skipper will translate all verbatims to ${translationsOptions
              .slice(1)
              .map(option => `<b>${option.key.toUpperCase()}</b>`)
              .join(', ')}.<br />All original responses will be preserved.</span>`}
            width="300px"
          />
        </div>
      </>
    ) : features.ai_assistants_oe_translation.disabled ? null : (
      <>
        <div className={`${style.row} ${style['section-title']}`}>Verbatim&nbsp;translations</div>
        <div className={`${style.row} p-fluid`}>
          <div className="p-field p-col-12">
            <Dropdown
              id="stat-view-options-translation-selector"
              className={classNames(
                'p-dropdown-rounded p-dropdown-left',
                'stat-view-options-translation-selector',
                '-dtr-stat-view-options-translation-selector'
              )}
              placeholder="Select..."
              value={translation || '*'}
              options={translationsOptions}
              optionValue="key"
              optionLabel="text"
              optionDisabled="disabled"
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              fixedDropdownDir="bottom"
              disabled={features.ai_assistants_oe_translation.disabled}
              onChange={
                features.ai_assistants_oe_translation.disabled ? null : event => onTranslationChange(event.value)
              }
              onShow={features.ai_assistants_oe_translation.disabled ? null : () => setShowTransationPopup(false)}
              onHide={features.ai_assistants_oe_translation.disabled ? null : () => setShowTransationPopup(true)}
            />
          </div>
        </div>
      </>
    )
  ) : null
}

Translations.propTypes = {
  root: PropTypes.any,
  model: PropTypes.object,
  data: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  onTranslationChange: PropTypes.func
}

Translations.displayName = 'Translations'

export default Translations
